'use client';
import React from 'react';
import { useSettings } from '@/context/SettingsContext';
import { useAerodrome } from '@/context/AerodromeContext';
import { Ruler, Wind, Thermometer, ArrowUp, Moon, Sun, Trash2, MapPin, Settings, ShieldCheck } from 'lucide-react';

// ─── Compact Toggle ──────────────────────────────────────────────────────────
interface ToggleProps {
  value: string;
  optA: { label: string; value: string };
  optB: { label: string; value: string };
  onChange: (v: string) => void;
}

function Toggle({ value, optA, optB, onChange }: ToggleProps) {
  const isA = value === optA.value;
  return (
    <div
      className="flex rounded-md overflow-hidden text-xs font-semibold"
      style={{ border: '1px solid var(--card-border)', backgroundColor: 'var(--card)' }}
    >
      <button
        onClick={() => onChange(optA.value)}
        className="px-3 py-1.5 transition-all duration-150"
        style={isA
          ? { background: 'linear-gradient(135deg,#7ec8e3,#b0d4e8)', color: '#080d1a' }
          : { color: 'var(--text-muted)', backgroundColor: 'transparent' }
        }
      >
        {optA.label}
      </button>
      <button
        onClick={() => onChange(optB.value)}
        className="px-3 py-1.5 transition-all duration-150"
        style={!isA
          ? { background: 'linear-gradient(135deg,#7ec8e3,#b0d4e8)', color: '#080d1a' }
          : { color: 'var(--text-muted)', backgroundColor: 'transparent' }
        }
      >
        {optB.label}
      </button>
    </div>
  );
}

// ─── Compact Row ─────────────────────────────────────────────────────────────
interface RowProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

function Row({ icon, label, children }: RowProps) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg"
      style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}
    >
      <div className="flex items-center gap-2">
        <span style={{ color: 'var(--info-blue)' }}>{icon}</span>
        <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-4">
      <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg,var(--border),transparent)' }} />
      <span className="text-[10px] font-bold uppercase tracking-widest px-2" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <div className="h-px flex-1" style={{ background: 'linear-gradient(270deg,var(--border),transparent)' }} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SettingsContent() {
  const { settings, updateSettings, icaoHistory, removeIcaoHistory } = useSettings();
  const { recentAerodromes, removeRecentAerodrome, setSelectedAerodrome, selectedAerodrome } = useAerodrome();

  const isVelivole = settings.theme === 'velivole';

  return (
    <div className="min-h-screen px-4 md:px-6 py-5 max-w-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <div
          className="p-2 rounded-lg"
          style={{ background: 'rgba(126,200,227,0.1)', border: '1px solid rgba(126,200,227,0.2)' }}
        >
          <Settings size={16} style={{ color: '#7ec8e3' }} />
        </div>
        <div>
          <h1 className="text-base font-bold" style={{ color: 'var(--text-secondary)' }}>Paramètres</h1>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Personnalisation de l'affichage</p>
        </div>
      </div>

      {/* ── Apparence ── */}
      <SectionHeader label="Apparence" />
      <div className="space-y-1.5">
        {/* Night theme */}
        <button
          onClick={() => updateSettings({ theme: 'night' })}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150"
          style={{
            backgroundColor: !isVelivole ? 'rgba(126,200,227,0.08)' : 'var(--card)',
            border: !isVelivole ? '1px solid rgba(126,200,227,0.35)' : '1px solid var(--card-border)',
          }}
        >
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(126,200,227,0.1)' }}>
            <Moon size={14} style={{ color: '#7ec8e3' }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-xs font-semibold" style={{ color: !isVelivole ? '#7ec8e3' : 'var(--text-secondary)' }}>Thème Nuit</p>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Dark Mode — fond sombre, contraste maximal</p>
          </div>
          {!isVelivole && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(126,200,227,0.15)', color: '#7ec8e3' }}>
              ACTIF
            </span>
          )}
        </button>

        {/* Vélivole theme */}
        <button
          onClick={() => updateSettings({ theme: 'velivole' })}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150"
          style={{
            backgroundColor: isVelivole ? 'var(--card)' : '#0d1a2e',
            border: isVelivole ? '1px solid var(--card-border)' : '1px solid #1a2e48',
          }}
        >
          <div className="p-1.5 rounded-md" style={{ backgroundColor: 'rgba(135,206,235,0.12)' }}>
            <Sun size={14} style={{ color: '#87ceeb' }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-xs font-semibold" style={{ color: isVelivole ? 'var(--info-blue)' : '#b0c8d8' }}>Thème Vélivole</p>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Fond bleu ciel pastel — cadres blancs, ambiance aérienne</p>
          </div>
          {isVelivole && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(135,206,235,0.15)', color: '#87ceeb' }}>
              ACTIF
            </span>
          )}
        </button>
      </div>

      {/* ── Unités ── */}
      <SectionHeader label="Unités de mesure" />
      <div className="space-y-1.5">
        <Row icon={<Ruler size={13} />} label="Altitude / Plafond">
          <Toggle
            value={settings.altitudeUnit}
            optA={{ label: 'm', value: 'm' }}
            optB={{ label: 'ft', value: 'ft' }}
            onChange={(v) => updateSettings({ altitudeUnit: v as 'm' | 'ft' })}
          />
        </Row>
        <Row icon={<ArrowUp size={13} />} label="Vitesse Verticale (VZ)">
          <Toggle
            value={settings.vzUnit}
            optA={{ label: 'm/s', value: 'm/s' }}
            optB={{ label: 'kts', value: 'kts' }}
            onChange={(v) => updateSettings({ vzUnit: v as 'm/s' | 'kts' })}
          />
        </Row>
        <Row icon={<Wind size={13} />} label="Vitesse du Vent">
          <Toggle
            value={settings.windUnit}
            optA={{ label: 'km/h', value: 'km/h' }}
            optB={{ label: 'kts', value: 'kts' }}
            onChange={(v) => updateSettings({ windUnit: v as 'km/h' | 'kts' })}
          />
        </Row>
        <Row icon={<Thermometer size={13} />} label="Température">
          <Toggle
            value={settings.tempUnit}
            optA={{ label: '°C', value: '°C' }}
            optB={{ label: '°F', value: '°F' }}
            onChange={(v) => updateSettings({ tempUnit: v as '°C' | '°F' })}
          />
        </Row>
      </div>

      {/* ── Sécurité ── */}
      <SectionHeader label="Sécurité — Cercle de Finesse" />
      <div
        className="rounded-lg px-3 py-3 space-y-2"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--card-border)' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span style={{ color: '#f97316' }}><ShieldCheck size={13} /></span>
            <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
              Marge de sécurité à l&apos;arrivée
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={settings.altitudeUnit === 'ft' ? 3000 : 1000}
              step={settings.altitudeUnit === 'ft' ? 50 : 10}
              value={settings.altitudeUnit === 'ft' ? Math.round(settings.safetyMargin * 3.281) : settings.safetyMargin}
              onChange={(e) => {
                const raw = parseInt(e.target.value, 10);
                if (isNaN(raw)) return;
                const metres = settings.altitudeUnit === 'ft' ? Math.round(raw / 3.281) : raw;
                updateSettings({ safetyMargin: Math.max(0, metres) });
              }}
              className="w-20 text-right text-xs font-mono rounded-md px-2 py-1 outline-none"
              style={{
                backgroundColor: 'var(--surface-elevated)',
                border: '1px solid var(--card-border)',
                color: '#f97316',
              }}
            />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
              {settings.altitudeUnit === 'ft' ? 'ft' : 'm'}
            </span>
          </div>
        </div>
        <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Soustraite du plafond AGL avant le calcul du cercle de finesse.{' '}
          Formule : <span style={{ color: '#f97316', fontFamily: 'monospace' }}>Rayon = (Plafond AGL − Marge) × Finesse</span>
        </p>
        <div className="flex items-center justify-between pt-1" style={{ borderTop: '1px solid var(--border)' }}>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Valeur actuelle</span>
          <span className="text-xs font-mono font-semibold" style={{ color: '#fbbf24' }}>
            {settings.safetyMargin} m / {Math.round(settings.safetyMargin * 3.281)} ft
          </span>
        </div>
      </div>

      {/* ── Mes Terrains ── */}
      <SectionHeader label="Mes Terrains" />
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: '1px solid var(--card-border)', backgroundColor: 'var(--card)' }}
      >
        {recentAerodromes.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-3">
            <MapPin size={13} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucun terrain consulté récemment</span>
          </div>
        ) : (
          recentAerodromes.map((aerodrome, idx) => {
            const isSelected = aerodrome.icao === selectedAerodrome.icao;
            return (
              <div
                key={aerodrome.icao}
                className="flex items-center justify-between px-3 py-2 group"
                style={{
                  borderTop: idx > 0 ? '1px solid var(--border)' : 'none',
                  backgroundColor: isSelected ? 'rgba(126,200,227,0.06)' : 'transparent',
                }}
              >
                <button
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  onClick={() => setSelectedAerodrome(aerodrome)}
                  title={`Sélectionner ${aerodrome.icao} — ${aerodrome.name}`}
                >
                  <MapPin size={11} style={{ color: isSelected ? '#7ec8e3' : 'var(--text-muted)' }} />
                  <span className="font-mono text-xs font-semibold" style={{ color: isSelected ? '#7ec8e3' : 'var(--text-secondary)' }}>
                    {aerodrome.icao}
                  </span>
                  <span className="text-xs truncate" style={{ color: isSelected ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                    {aerodrome.name}
                  </span>
                </button>
                <button
                  onClick={() => removeRecentAerodrome(aerodrome.icao)}
                  className="p-1 rounded opacity-40 group-hover:opacity-100 transition-opacity hover:bg-red-500/20 flex-shrink-0"
                  style={{ color: '#f87171' }}
                  title={`Supprimer ${aerodrome.icao}`}
                  aria-label={`Supprimer ${aerodrome.icao}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })
        )}
      </div>
      <p className="text-[10px] mt-1 px-1" style={{ color: 'var(--text-muted)' }}>
        Les 10 derniers terrains consultés — cliquez pour sélectionner, poubelle pour supprimer
      </p>

      {/* ── Aperçu Double Unité ── */}
      <SectionHeader label="Aperçu — Double Unité" />
      <div
        className="rounded-lg p-3"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-border)',
        }}
      >
        <div className="grid grid-cols-4 gap-3">
          {[
            {
              label: 'Base Cu',
              primary: settings.altitudeUnit === 'm' ? '1 800 m' : '5 906 ft',
              secondary: settings.altitudeUnit === 'm' ? '5 906 ft' : '1 800 m',
            },
            {
              label: 'VZ max',
              primary: settings.vzUnit === 'm/s' ? '2.5 m/s' : '4.9 kts',
              secondary: settings.vzUnit === 'm/s' ? '4.9 kts' : '2.5 m/s',
            },
            {
              label: 'Vent sol',
              primary: settings.windUnit === 'km/h' ? '24 km/h' : '13 kts',
              secondary: settings.windUnit === 'km/h' ? '13 kts' : '24 km/h',
            },
            {
              label: 'Temp.',
              primary: settings.tempUnit === '°C' ? '22 °C' : '72 °F',
              secondary: settings.tempUnit === '°C' ? '72 °F' : '22 °C',
            },
          ].map((item) => (
            <div
              key={item.label}
              className="flex flex-col items-center gap-0.5 py-2 rounded-lg"
              style={{ backgroundColor: 'var(--surface-elevated)', border: '1px solid var(--border)' }}
            >
              <span className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
              <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>{item.primary}</span>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{item.secondary}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="h-8" />
    </div>
  );
}
